import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { Button } from "../components/ui/Button/Button";
import { Input } from "../components/ui/Input/Input";
import { Card } from "../components/ui/Card/Card";
import { MessageStrip } from "../components/ui/MessageStrip/MessageStrip";
import { Title, Icon } from "@ui5/webcomponents-react";
import { ROUTES } from "../constants/routes";
import { cn } from "../lib/cn";
import "@ui5/webcomponents-icons/dist/employee.js";
import "@ui5/webcomponents-icons/dist/locked.js";
import "@ui5/webcomponents-icons/dist/show.js";
import "@ui5/webcomponents-icons/dist/hide.js";

/**
 * Modern Login Page
 * Clean implementation using new component architecture
 */
export default function LoginNew() {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const { login, error, clearError, isAuthenticated } = useAuth();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || ROUTES.DASHBOARD;
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // Clear errors on mount
  useEffect(() => {
    clearError();
  }, [clearError]);

  const validateForm = () => {
    const errors = {};

    if (!formData.email) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    if (!formData.password) {
      errors.password = "Password is required";
    } else if (formData.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear field validation error
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: "" }));
    }

    // Clear global error
    if (error) {
      clearError();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    const result = await login(formData.email, formData.password);

    if (result.success) {
      showSuccess("Login successful! Welcome back.");
      const from = location.state?.from?.pathname || ROUTES.DASHBOARD;
      navigate(from, { replace: true });
    } else {
      showError(result.error || "Login failed. Please check your credentials.");
    }

    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0854a0] via-[#1976d2] to-[#2196f3] flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-in-up">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-20 h-20 mx-auto mb-6 bg-white/95 rounded-full flex items-center justify-center shadow-xl backdrop-blur-lg border border-white/20">
            <Icon name="employee" className="text-4xl text-[#0854a0]" />
          </div>

          <Title
            level="H1"
            className="text-white text-4xl font-light mb-3 drop-shadow-md"
          >
            conRIO
          </Title>

          <p className="text-white/95 text-lg">Sign in to your account</p>
        </div>

        {/* Login Card */}
        <Card className="bg-white/98 backdrop-blur-xl shadow-2xl border border-white/20">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error Message */}
            {error && (
              <MessageStrip design="Negative" hideCloseButton>
                {error}
              </MessageStrip>
            )}

            {/* Email Input */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Email Address
              </label>
              <Input
                type="Email"
                placeholder="Enter your email"
                value={formData.email}
                onInput={(e) => handleInputChange("email", e.target.value)}
                valueState={validationErrors.email ? "Error" : "None"}
                valueStateMessage={validationErrors.email}
                icon="employee"
                disabled={isSubmitting}
              />
            </div>

            {/* Password Input */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? "Text" : "Password"}
                  placeholder="Enter your password"
                  value={formData.password}
                  onInput={(e) => handleInputChange("password", e.target.value)}
                  valueState={validationErrors.password ? "Error" : "None"}
                  valueStateMessage={validationErrors.password}
                  icon="locked"
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  <Icon name={showPassword ? "hide" : "show"} />
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              design="Emphasized"
              className="w-full"
              disabled={isSubmitting}
              onClick={(e) => {
                e.preventDefault();
                handleSubmit(e);
              }}
            >
              {isSubmitting ? "Signing in..." : "Sign In"}
            </Button>

            {/* Sign Up Link */}
            <div className="text-center text-sm text-gray-600">
              Don't have an account?{" "}
              <Link
                to={ROUTES.SIGNUP}
                className="text-[#0854a0] hover:underline font-medium"
              >
                Sign up
              </Link>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}
